import React, { useRef, useState, useEffect } from 'react';

export default function Acknowledgment({ userData, onComplete }) {
  const canvasRef = useRef(null);
  const [isSigning, setIsSigning] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1B3A5C';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsSigning(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isSigning) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDraw = (e) => {
    e.preventDefault();
    setIsSigning(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    const signatureData = hasSigned ? canvas.toDataURL('image/png') : null;
    onComplete({
      acknowledgedAt: new Date().toISOString(),
      signatureData,
    });
  };

  return (
    <div className="page-container">
      <div style={{ marginBottom: 20 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 4 of 5 — Acknowledgment</div>
        <h1 className="page-title">HIPAA &amp; Texas Privacy Training Acknowledgment</h1>
        <p className="page-subtitle">Please read the acknowledgment below, sign, and click <strong>Submit &amp; Get Certificate</strong>.</p>
      </div>

      <div className="card" style={{ marginBottom: 24, padding: '32px 36px', borderLeft: '5px solid #1B3A5C' }}>
        <p style={{ color: '#2C3E50', fontSize: '14px', lineHeight: '1.9', marginBottom: 0 }}>
          I acknowledge that I have completed the organization's HIPAA and Texas privacy training.
          I understand the importance of protecting patient health information (PHI) and my
          responsibilities under applicable federal and state laws, including HIPAA and Texas
          privacy regulations.
          <br /><br />
          I have reviewed and understand the organization's policies regarding privacy, security,
          access to patient information, and incident reporting. I agree to comply with these
          policies as a condition of my employment.
          <br /><br />
          I understand that I am required to report any suspected or actual privacy or security
          incident immediately to the appropriate personnel and that failure to comply with these
          requirements may result in disciplinary action.
          <br /><br />
          I acknowledge that violations of these policies may result in sanctions, up to and
          including termination.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24, padding: '24px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 700, color: '#1B3A5C', fontSize: 13, marginBottom: 6 }}>Employee Name</label>
            <div style={{ padding: '10px 14px', background: '#F5F7FA', border: '1px solid #DDE3ED', borderRadius: 6, fontSize: 14, color: '#2C3E50' }}>
              {userData.fullName}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, color: '#1B3A5C', fontSize: 13, marginBottom: 6 }}>Date</label>
            <div style={{ padding: '10px 14px', background: '#F5F7FA', border: '1px solid #DDE3ED', borderRadius: 6, fontSize: 14, color: '#2C3E50' }}>
              {today}
            </div>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 700, color: '#1B3A5C', fontSize: 13, marginBottom: 6 }}>
            E-Signature <span style={{ fontWeight: 400, color: '#78909C' }}>(Optional — draw your signature below)</span>
          </label>
          <div style={{ position: 'relative', border: '2px dashed #B0BEC5', borderRadius: 8, background: '#FAFBFC', overflow: 'hidden' }}>
            <canvas
              ref={canvasRef}
              width={700}
              height={160}
              style={{ display: 'block', width: '100%', height: 160, cursor: 'crosshair', touchAction: 'none' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            {!hasSigned && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                color: '#B0BEC5', fontSize: 14, pointerEvents: 'none', userSelect: 'none',
              }}>
                Sign here with your mouse or finger
              </div>
            )}
          </div>
          {hasSigned && (
            <button
              onClick={clearSignature}
              style={{ marginTop: 8, background: 'none', border: 'none', color: '#78909C', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
            >
              Clear signature
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 28, padding: '18px 24px', background: '#EEF3F8' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ marginTop: 2, width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: 14, color: '#2C3E50', lineHeight: 1.6 }}>
            I confirm that I have read, understood, and agree to the HIPAA &amp; Texas Privacy Training Acknowledgment above.
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-secondary"
          onClick={handleSubmit}
          disabled={!agreed}
          style={{ opacity: agreed ? 1 : 0.5 }}
        >
          Submit &amp; Get Certificate →
        </button>
      </div>
    </div>
  );
}
